package org.dronda.app.web.ui.nav

import androidx.compose.runtime.MutableState
import androidx.compose.runtime.State
import kotlinx.browser.window
import org.dronda.app.web.ui.Route

/**
 * Router manages URL-based navigation with browser history integration.
 * Converts between Route objects and URL paths, and synchronizes browser history.
 *
 * @param routeState The shared mutable state that holds the current route.
 *                   This state is also observed by NavHost for rendering.
 */
class Router(
    private val routeState: MutableState<Route>
) {
    val currentRoute: State<Route> = routeState

    /**
     * Navigate to a new route and update browser history.
     * @param route The route to navigate to
     * @param replace If true, replaces current history entry instead of creating new one
     */
    fun navigate(route: Route, replace: Boolean = false) {
        routeState.value = route
        val path = routeToPath(route)
        if (replace) {
            window.history.replaceState(null, "", path)
        } else {
            window.history.pushState(null, "", path)
        }
    }

    /**
     * Handle browser back/forward button navigation (popstate event).
     * Updates current route based on URL path without modifying history.
     */
    fun handlePopState(path: String) {
        routeState.value = pathToRoute(path)
    }

    /**
     * Convert a Route object to a URL path.
     */
    private fun routeToPath(route: Route): String = when (route) {
        is Route.Events -> "/events"
        is Route.Login -> "/login"
        is Route.LocationConfig -> "/location-config"
        is Route.EventCreate -> "/events/new"
        is Route.EventDetail -> "/events/${route.eventId}"
        is Route.EventAdmin -> "/events/${route.eventId}/admin"
        is Route.Wishlist -> "/events/${route.eventId}/wishlist/${route.wishlistId}"
        is Route.NotFound -> "/404"
    }

    companion object {
        /**
         * Parse the initial route from the current browser URL.
         * Useful for initializing the router on app load.
         */
        fun fromCurrentUrl(): Route {
            return pathToRoute(window.location.pathname)
        }

        /**
         * Parse a URL path and convert it to a Route object.
         * Returns Route.NotFound for invalid paths.
         */
        private fun pathToRoute(path: String): Route {
            val normalizedPath = path.trimStart('/')
            val segments = if (normalizedPath.isEmpty()) emptyList() else normalizedPath.split('/')

            return when {
                path == "/" || path == "/events" || normalizedPath.isEmpty() -> Route.Events
                path == "/login" -> Route.Login
                path == "/location-config" -> Route.LocationConfig
                path == "/events/new" -> Route.EventCreate
                segments.size == 3 && segments[0] == "events" && segments[2] == "admin" ->
                    Route.EventAdmin(segments[1])
                segments.size == 2 && segments[0] == "events" ->
                    Route.EventDetail(segments[1])
                segments.size == 4 && segments[0] == "events" && segments[2] == "wishlist" ->
                    Route.Wishlist(wishlistId = segments[3], eventId = segments[1])
                else -> Route.NotFound
            }
        }
    }
}
